import { useContext, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import IconPicker from "components/icons/IconPicker";
import { useRelationshipTargets } from "hooks/useRelationshipTargets";
import { deriveFieldId, deriveSingular } from "routes/custom_tables/helpers";

import { ImportTargetContext } from "../ImportTargetContext";
import MappingRow from "../MappingRow";
import {
  applyMappingForAppend,
  applyMappingToSchema,
  validateAppendMapping,
  validateMapping,
} from "../helpers";

const Step3Mapping = ({ onNext, onBack }) => {
  const {
    mode,
    existingSchema,
    parseResult,
    mapping,
    setMapping,
    targetDisplayName,
    setTargetDisplayName,
    targetSingularName,
    setTargetSingularName,
    targetIconName,
    setTargetIconName,
    targetDescription,
    setTargetDescription,
  } = useContext(ImportTargetContext);
  const isAppend = mode === "append";
  const [rows, setRows] = useState(() => mapping ?? []);
  const [singularDirty, setSingularDirty] = useState(
    !!targetSingularName &&
      targetSingularName !== deriveSingular(targetDisplayName)
  );

  const handleDisplayNameChange = value => {
    setTargetDisplayName(value);
    if (!singularDirty) setTargetSingularName(deriveSingular(value));
  };

  const handleSingularChange = value => {
    setTargetSingularName(value);
    setSingularDirty(true);
  };

  const handleResetSingular = () => {
    setTargetSingularName(deriveSingular(targetDisplayName));
    setSingularDirty(false);
  };

  const derivedSlug = useMemo(
    () => deriveFieldId(targetDisplayName ?? ""),
    [targetDisplayName]
  );

  useEffect(() => {
    // Re-entering from the next step restores the committed rows on mount;
    // re-deriving from inference here would discard the operator's edits.
    if (mapping) return;
    if (parseResult) {
      if (isAppend) {
        setRows(applyMappingForAppend(parseResult, existingSchema, {}));
      } else {
        setRows(applyMappingToSchema(parseResult, {}));
      }
    }
  }, [parseResult, isAppend, existingSchema, mapping]);

  const errors = useMemo(() => {
    if (isAppend) {
      return validateAppendMapping(rows, existingSchema);
    }
    return validateMapping(rows);
  }, [rows, isAppend, existingSchema]);

  // Append mode inherits relationship options from the existing schema, so
  // only new-table imports with a relationship column pay for the targets
  // list call.
  const hasRelationshipRow =
    !isAppend && rows.some(r => r.include && r.type === "relationship");
  const { targetOptions, labelFieldOptionsFor } =
    useRelationshipTargets(hasRelationshipRow);

  // The synthetic ``id`` column lives at the top of the picker so the
  // operator's eye lands on the upsert key first. Backend already
  // prepends it in append-mode payloads; this sort is defensive against
  // re-ordering changes upstream.
  const sortedExistingColumns = useMemo(() => {
    const cols = existingSchema?.columns ?? [];
    return [...cols].sort((a, b) => {
      if (a.field_id === "id") return -1;
      if (b.field_id === "id") return 1;
      return 0;
    });
  }, [existingSchema]);
  const errorByIndex = useMemo(() => {
    const map = new Map();
    for (const e of errors) {
      if (e.row_index == null) continue;
      if (!map.has(e.row_index)) {
        map.set(e.row_index, e.message);
      }
    }
    return map;
  }, [errors]);

  const updateRow = (index, nextRow) => {
    setRows(prev => prev.map((r, i) => (i === index ? nextRow : r)));
  };

  const handleNext = () => {
    setMapping(rows);
    onNext?.();
  };

  if (!parseResult) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
        <p className="tw:text-muted-foreground tw:m-0">No file uploaded yet.</p>
        <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6">
      <h2 className="tw:font-heading tw:text-foreground tw:m-0 tw:text-2xl">
        {isAppend
          ? `Map your CSV to ${existingSchema?.display_name ?? "the existing table"}`
          : "Review the proposed schema"}
      </h2>
      <p className="tw:text-muted-foreground tw:m-0">
        {isAppend
          ? "Pick which existing column each CSV column maps to. Unmapped columns will be skipped."
          : "We've inferred a type for each column. Adjust as needed, then continue."}
      </p>

      <div className="tw:overflow-x-auto tw:overflow-y-hidden">
        <div className="tw:w-full tw:grid tw:grid-cols-[4rem_minmax(8rem,12rem)_minmax(8rem,12rem)_minmax(6rem,10rem)_minmax(9rem,10rem)_6rem_5.5rem_minmax(8rem,1fr)] tw:gap-3 tw:px-4 tw:py-2 tw:bg-accent tw:text-xs tw:uppercase tw:tracking-wider tw:text-muted-foreground tw:font-semibold tw:border tw:border-border tw:border-b-0 tw:rounded-t-md">
          <span>Include</span>
          <span>CSV column</span>
          <span>Display name</span>
          <span>{isAppend ? "Existing column" : "Field id"}</span>
          <span>Type</span>
          <span>Required</span>
          <span>Unique</span>
          <span>Samples</span>
        </div>

        <div className="tw:w-full tw:flex tw:flex-col tw:border tw:border-border tw:rounded-md tw:overflow-hidden tw:bg-card">
          {rows.map((row, index) => (
            <MappingRow
              key={`${index}-${row.csv_column_name}`}
              row={row}
              onChange={next => updateRow(index, next)}
              error={errorByIndex.get(index) ?? null}
              existingColumns={isAppend ? sortedExistingColumns : null}
              relationshipTargetOptions={targetOptions}
              labelFieldOptionsFor={labelFieldOptionsFor}
            />
          ))}
        </div>
      </div>

      {!isAppend && (
        <>
          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mt-4 tw:max-md:grid-cols-1">
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel
                htmlFor="import-table-name"
                className="tw:text-muted-foreground tw:text-sm tw:font-semibold"
              >
                Name
              </FieldLabel>
              <Input
                id="import-table-name"
                value={targetDisplayName ?? ""}
                onChange={e => handleDisplayNameChange(e.target.value)}
                placeholder="UTM Sources"
              />
              <span className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                Plural form. Shown in nav and headers.
              </span>
            </Field>
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel
                htmlFor="import-table-singular"
                className="tw:text-muted-foreground tw:text-sm tw:font-semibold"
              >
                Singular name
              </FieldLabel>
              <Input
                id="import-table-singular"
                value={targetSingularName ?? ""}
                onChange={e => handleSingularChange(e.target.value)}
                placeholder="UTM Source"
              />
              <span className="tw:text-muted-foreground tw:text-xs tw:leading-snug">
                {singularDirty ? (
                  <>
                    Used in &quot;New {targetSingularName || "Item"}&quot; and
                    &quot;Edit {targetSingularName || "Item"}&quot; titles.{" "}
                    <Button
                      variant="link"
                      className="tw:h-auto tw:p-0 tw:text-xs"
                      onClick={handleResetSingular}
                    >
                      Reset to auto-derived
                    </Button>
                  </>
                ) : (
                  <>
                    Auto-derived from the name. Override for irregular plurals.
                  </>
                )}
              </span>
            </Field>
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mt-4 tw:max-md:grid-cols-1">
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel className="tw:text-muted-foreground tw:text-sm tw:font-semibold">
                Icon
              </FieldLabel>
              <IconPicker value={targetIconName} onChange={setTargetIconName} />
            </Field>
            <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:min-w-0">
              <FieldLabel className="tw:text-muted-foreground tw:text-sm tw:font-semibold">
                Table ID
              </FieldLabel>
              <Badge variant="secondary" code>
                {derivedSlug || "—"}
              </Badge>
            </Field>
          </div>

          <Field className="tw:flex tw:flex-col tw:gap-1.5 tw:mt-4">
            <FieldLabel
              htmlFor="import-table-description"
              className="tw:text-muted-foreground tw:text-sm tw:font-semibold"
            >
              Description
            </FieldLabel>
            <Input
              id="import-table-description"
              value={targetDescription ?? ""}
              onChange={e => setTargetDescription(e.target.value)}
              placeholder="Optional description"
            />
          </Field>
        </>
      )}

      <div className="tw:flex tw:justify-between tw:gap-3 tw:pt-3 tw:border-t tw:border-border">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleNext} disabled={errors.length > 0}>
          Next
        </Button>
      </div>
    </div>
  );
};

export default Step3Mapping;
